/**
 * Next-Step Guidance Engine
 * Provides contextual "what happens next" and "who owns it" for each entity state.
 * Uses centralized workflow engine for stage awareness.
 */

import type { Job, Issue } from "./types";
import type { Opportunity, OpportunityStage } from "./opportunityTypes";
import { isWonStage, isActiveStage, OPPORTUNITY_STAGES, formatDisplayReference } from "./opportunityTypes";
import type { ServiceTicket } from "./serviceTicketTypes";
import { getStageConfig, getNextStages } from "./workflowEngine";

export interface NextStep {
  action: string;
  owner: string;
  priority: "high" | "medium" | "low";
  link?: string;
}

/* ═══════ OPPORTUNITY (replaces Lead + Quote) ═══════ */
export function getOpportunityNextSteps(opp: Opportunity): NextStep[] {
  const steps: NextStep[] = [];
  const hasConsultationData = Boolean(
    opp.projectConfig ||
    (opp.consultationProgress?.completedScopes?.length ?? 0) > 0 ||
    (opp.consultationProgress?.currentStep ?? 0) > 0 ||
    (opp.totalEstimate ?? 0) > 0
  );
  const hasSignedContract = opp.contract?.status === "Signed";
  const map: Partial<Record<OpportunityStage, NextStep[]>> = {
    "Lead": [
      { action: "Make contact — leads go cold fast", owner: "Sales Rep", priority: "high" },
      { action: "Verify info — bad data wastes appointments", owner: "Sales Rep", priority: "medium" },
    ],
    "Appointment Scheduled": [
      { action: "Confirm with customer — reduces no-shows", owner: "Sales Rep", priority: "high" },
      { action: "Prepare for consultation", owner: "Sales Rep", priority: "medium" },
    ],
    "Appointment Completed": hasSignedContract
      ? [
          { action: "Close Won and create job", owner: "Sales Rep", priority: "high", link: `/opportunities/${opp.id}` },
          { action: "Hand off to Operations after conversion", owner: "Sales Rep", priority: "medium" },
        ]
      : hasConsultationData
      ? [
          { action: "Set outcome (Won / Follow-Up / Lost)", owner: "Sales Rep", priority: "high", link: `/opportunities/${opp.id}` },
          { action: "Follow up if undecided — deal at risk", owner: "Sales Rep", priority: "medium" },
        ]
      : [
          { action: "Complete consultation data before outcome", owner: "Sales Rep", priority: "high", link: `/opportunities/${opp.id}` },
          { action: "Follow up if undecided — deal at risk", owner: "Sales Rep", priority: "medium" },
        ],
    "Follow-Up Needed": [
      { action: "Complete follow-up — deal going cold", owner: opp.followUpType === "manager" ? "Manager" : "Sales Rep", priority: "high" },
      { action: "Re-present pricing if needed", owner: "Sales Rep", priority: "medium", link: `/opportunities/${opp.id}` },
    ],
    "Closed Won": [
      { action: "Start job execution — revenue depends on it", owner: "Operations", priority: "high", link: opp.linkedJobId ? `/jobs/${opp.linkedJobId}` : undefined },
    ],
    "Closed Lost": [],
  };
  steps.push(...(map[opp.stage] || []));
  if (!opp.assignedRep) steps.unshift({ action: "Assign rep — unowned lead = lost lead", owner: "Admin", priority: "high" });
  return steps;
}

// Backward compat aliases
export const getLeadNextSteps = (opp: Opportunity) => getOpportunityNextSteps(opp);
export const getQuoteNextSteps = (opp: Opportunity) => getOpportunityNextSteps(opp);

/* ═══════ JOB ═══════ */
export function getJobNextSteps(job: Job, openIssues: Issue[], opportunities: Opportunity[]): NextStep[] {
  const steps: NextStep[] = [];
  const sourceOpp = opportunities.find(o => o.linkedJobId === job.id);
  const stageConfig = getStageConfig(job.currentStage);
  const nextStages = getNextStages(job.currentStage);

  switch (job.currentStage) {
    case "Sale Closed":
      if (job.rescissionStatus === "In Rescission")
        steps.push({ action: "Rescission period active — job on hold", owner: "System", priority: "high" });
      else
        steps.push({ action: "Start review — delays push out install date", owner: "Operations", priority: "high", link: `/jobs/${job.id}` });
      break;

    case "Review and Validation":
      if (sourceOpp && (!sourceOpp.contract || sourceOpp.contract.status !== "Signed"))
        steps.push({ action: "Contract unsigned — job blocked", owner: "Admin / Sales", priority: "high" });
      steps.push({ action: "Validate scope — ordering depends on this", owner: "Operations", priority: "high", link: `/jobs/${job.id}` });
      if (job.permitRequired && job.permitStatus === "Not Required")
        steps.push({ action: "Submit permit — install blocked without it", owner: "Operations", priority: "high", link: "/permitting" });
      steps.push({ action: "Order materials — schedule depends on delivery", owner: "Procurement", priority: "high", link: "/ordering-queue" });
      break;

    case "Permit and Order Pending":
      if (job.permitRequired && job.permitStatus !== "Approved")
        steps.push({ action: "Permit pending — install date at risk", owner: "Operations", priority: "high", link: "/permitting" });
      if (job.orderStatus !== "ETA Received" && job.orderStatus !== "Confirmed")
        steps.push({ action: "Materials unconfirmed — schedule blocked", owner: "Procurement / Warehouse", priority: "high", link: "/warehouse" });
      break;

    case "Ready for Scheduling":
      steps.push({ action: "Schedule now — revenue delayed until installed", owner: "Operations", priority: "high", link: "/scheduling" });
      if (job.assignmentType === "Unassigned")
        steps.push({ action: "Assign crew — no install without a team", owner: "Operations", priority: "high", link: "/scheduling-dispatch" });
      break;

    case "Scheduled Confirmed":
      steps.push({ action: "Prep materials — install tomorrow depends on this", owner: "Warehouse / Install", priority: "medium", link: "/warehouse" });
      steps.push({ action: "Confirm crew — no-show risks rescheduling", owner: "Operations", priority: "medium", link: "/scheduling-dispatch" });
      break;

    case "In Progress":
      steps.push({ action: "Monitor install — delays increase cost", owner: "Install Lead", priority: "medium" });
      if (openIssues.length > 0)
        steps.push({ action: `${openIssues.length} issue(s) blocking progress — resolve now`, owner: "Operations / Install", priority: "high", link: `/jobs/${job.id}` });
      steps.push({ action: "Submit QA — closeout blocked until reviewed", owner: "Install Lead / QA", priority: "high", link: `/jobs/${job.id}` });
      break;

    case "QA Review":
      if (!job.qaSubmitted)
        steps.push({ action: "QA not submitted — closeout stalled", owner: "Install Lead / QA", priority: "high", link: `/jobs/${job.id}` });
      if (openIssues.length > 0)
        steps.push({ action: `${openIssues.length} issue(s) remain — revenue held`, owner: "Operations", priority: "high", link: `/jobs/${job.id}` });
      else
        steps.push({ action: "Approve and close — revenue recognition waiting", owner: "Operations", priority: "high", link: "/closeout" });
      break;

    case "Completed":
      if (job.closeoutStatus !== "Closed")
        steps.push({ action: "Finalize closeout — revenue not recognized", owner: "Operations / Admin", priority: "medium", link: "/closeout" });
      if (!job.customerSignoff)
        steps.push({ action: "Get sign-off — protects against disputes", owner: "Sales / Operations", priority: "medium", link: `/jobs/${job.id}` });
      break;
  }

  return steps;
}

/* ═══════ SERVICE TICKET ═══════ */
export function getServiceTicketNextSteps(ticket: ServiceTicket): NextStep[] {
  const steps: NextStep[] = [];

  switch (ticket.status) {
    case "Open":
      if (!ticket.assignedTo)
        steps.push({ action: "Assign tech — customer waiting", owner: "Service Manager", priority: "high" });
      steps.push({ action: "Begin investigation — SLA clock running", owner: ticket.assignedTo || "Service", priority: "high" });
      break;
    case "In Progress":
      steps.push({ action: "Complete repair — customer impact ongoing", owner: ticket.assignedTo || "Service", priority: "high" });
      if (ticket.priority === "Urgent")
        steps.push({ action: "Escalate today — urgent unresolved", owner: "Service Manager", priority: "high" });
      break;
    case "Awaiting Parts":
      steps.push({ action: "Track parts — return visit blocked", owner: "Warehouse / Procurement", priority: "high" });
      steps.push({ action: "Schedule return when parts arrive", owner: "Service", priority: "medium" });
      break;
    case "Scheduled":
      steps.push({ action: "Confirm visit with customer", owner: "Service", priority: "medium" });
      steps.push({ action: "Complete service — close the loop", owner: ticket.assignedTo || "Service", priority: "high" });
      break;
    case "Resolved":
      steps.push({ action: "Verify satisfaction and close ticket", owner: "Service Manager", priority: "low" });
      break;
  }

  return steps;
}

/* ═══════ CUSTOMER AGGREGATE ═══════ */
export function getCustomerNextSteps(
  customerJobs: Job[],
  customerOpps: Opportunity[],
  customerTickets: ServiceTicket[],
  allIssues: Issue[],
  allOpps: Opportunity[],
): NextStep[] {
  const steps: NextStep[] = [];
  const isOpportunityStage = (stage: unknown): stage is OpportunityStage =>
    typeof stage === "string" && (OPPORTUNITY_STAGES as string[]).includes(stage);

  // Active opportunities
  for (const o of customerOpps) {
    const stageCandidate = (o as any).stage ?? (o as any).status;
    if (!isOpportunityStage(stageCandidate) || !isActiveStage(stageCandidate)) continue;
    const normalizedOpp = { ...(o as any), stage: stageCandidate } as Opportunity;
    const oSteps = getOpportunityNextSteps(normalizedOpp);
    if (oSteps.length > 0) {
      const ref =
        (o as any).displayReference ||
        (o as any).quoteNumber ||
        (o as any).quote_number ||
        formatDisplayReference(o.id);
      steps.push({ ...oSteps[0], action: `[${ref}] ${oSteps[0].action}` });
    }
  }
  // Active jobs
  for (const j of customerJobs.filter(j => j.currentStage !== "Completed")) {
    const jobIssues = allIssues.filter(i => i.jobId === j.id && i.status !== "Closed" && i.status !== "Resolved");
    const jSteps = getJobNextSteps(j, jobIssues, allOpps);
    if (jSteps.length > 0) steps.push({ ...jSteps[0], action: `[${j.jobNumber || j.id}] ${jSteps[0].action}` });
  }
  // Active tickets
  for (const t of customerTickets.filter(t => !["Resolved", "Closed"].includes(t.status))) {
    const tSteps = getServiceTicketNextSteps(t);
    if (tSteps.length > 0) steps.push({ ...tSteps[0], action: `[${t.id}] ${tSteps[0].action}` });
  }
  return steps.slice(0, 6);
}
